/* The gallery manager.
 *
 *   Gallery         a named set of albums
 *     └ Album       photos, OR sub-albums — one or the other, never both
 *         └ Sub-album   optional, one level, never more
 *
 * An album holding both was the original design, on the reasoning that nobody
 * should have to reorganise to add their first sub-album. In practice it read as
 * two half-empty lists stacked on one screen: an album of photos still showed an
 * empty "Sub-albums" heading above them, and "where does the next photo go?" had
 * no clear answer. The first thing put in an album now decides which kind it is,
 * and the server refuses the other kind from then on.
 *
 * MOST CLIENTS NEVER SEE THE WORD "GALLERY". Their site has exactly one, so the
 * manager opens on its ALBUMS: no list to get past first, and no "new gallery"
 * button offering a second one the site would ignore. Several galleries is a
 * per-client layout a Triton admin sets, and only then is the list the way in.
 *
 * An album is either PHOTOS or BEFORE/AFTER PAIRS, chosen when it is made — but
 * a photos album can still hold the odd pair, because gallery_items is one table
 * with a type discriminant and one sort sequence (migration 0060). That is what
 * makes a pair droppable between two photos and unsplittable when reordered; two
 * tables could do neither. So the kind of an album is about what it is FOR, not
 * about what it is allowed to contain.
 *
 * Photos always live in an album, so a new gallery starts with NONE and the
 * first thing it asks for is one. Nothing is auto-created on the client's
 * behalf: an earlier version made an implicit album per gallery and named it
 * after the gallery, which showed up as a phantom duplicate the moment anyone
 * looked at the album list. The container gallery on a single-gallery layout is
 * the one exception — it is not something the client is choosing, so their first
 * album mints it rather than making them name a box they will never be shown.
 *
 * There is no publish button anywhere. The client's site reads this through the
 * public API and renders on demand, so a photo is live when its upload finishes
 * and a reorder is live when the arrow is clicked.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { processImageSized, rotateImage } from '@/lib/image';
import { apiBlob } from '@/data/api';
import {
  createGalleryAlbum, createGalleryPair, deleteGalleryCategory,
  deleteGalleryItem, getGallery, rotateGalleryAsset, getGalleryDetail, reorderGalleryAlbums, reorderGalleryItems,
  reorderSubAlbums,
  updateGalleryCategory, updateGalleryItem, uploadGalleryPhoto, uploadGallerySide,
  type GalleryItem, type GalleryKind,
} from '@/data/client';
import { useGalleries } from './useGalleries';
import { SaveBar } from './SaveBar';
import { Modal } from './Modal';
import { AssetImg } from './AssetImg';
import { GalleryApiCard } from './GalleryApiCard';
import { GallerySettingsButton } from './GallerySettings';
import { Hint } from './Hint';
import { DragGrip, MoveButtons, useMoveOrder } from './MoveButtons';
import { useDragOrder } from './useDragOrder';

const msg = (e: unknown, fallback: string) =>
  e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : fallback;

/* Two levels, because a client has one gallery and never sees it: albums, and
   the sub-albums an album can be split into. The gallery is the container the
   site reads its photos out of, not somewhere the client navigates. */
type View =
  | { at: 'albums' }
  /* An album: either its photos or the sub-albums it is split into. */
  | { at: 'album'; id: string }
  /* A sub-album: just photos, so it reuses ItemsView unchanged. */
  | { at: 'subalbum'; id: string; albumId: string };

export function GalleryManager({ onBack }: { onBack: () => void }) {
  const [view, setView] = useState<View>({ at: 'albums' });
  const { galleryId, loading } = useGalleries();
  const [saveErr, setSaveErr] = useState('');

  const screen = view.at === 'album'
    ? <AlbumView albumId={view.id}
        onBack={() => setView({ at: 'albums' })}
        onOpenSub={(id) => setView({ at: 'subalbum', id, albumId: view.id })} />
    : view.at === 'subalbum'
      ? <ItemsView categoryId={view.id}
          onBack={() => setView({ at: 'album', id: view.albumId })} />
      : loading
        ? <div className="es-wrap"><p className="es-hint">Loading…</p></div>
        : <GalleryView id={galleryId} onBack={onBack}
            onOpenCategory={(id) => setView({ at: 'album', id })} />;

  /* Above every screen, not at the end of one. Someone can upload on one screen,
     reorder on another and rename on a third; what those have in common is that
     none of it is on the site yet. */
  return (
    <>
      <SaveBar onError={setSaveErr} />
      {saveErr && <p className="vault-err"><Icon name="alert-circle" size={13} /> {saveErr}</p>}
      {screen}
    </>
  );
}

/* Add an album. Separate from the photo screens because there
   is nothing to choose here — the gallery is the one you are looking at. */
function AddAlbumDialog({ parentId, title = 'New album', onClose, onAdded }: {
  /** Set when this is a sub-album; its parent is what places it. */
  parentId?: string;
  title?: string;
  onClose: () => void; onAdded: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  /* Fixed at creation. Changing it later would leave whichever kind of item does
     not match stranded in an album that no longer offers a way to add more. */
  const [kind, setKind] = useState<GalleryKind>('standard');
  const [err, setErr] = useState('');
  const add = useMutation({
    mutationFn: async () => {
      return createGalleryAlbum({ name: name.trim(), kind, parentId });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['galleries'] });
      onAdded(); onClose();
    },
    onError: (e) => setErr(msg(e, 'Could not add the album.')),
  });
  return (
    <Modal title={title} onClose={onClose}>
      <div className="es-modal__body">
        <span className="es-label">
          Name
          <Hint label={parentId ? 'What is a sub-album?' : 'What is an album?'}>
            {parentId
              ? 'Sub-albums split this album into sections. Photos then live in the sub-albums rather than in the album itself.'
              : 'Photos go inside albums. You can have as many as you need.'}
          </Hint>
        </span>
        <Input placeholder={parentId ? 'e.g. Trex' : 'e.g. Residential Roofing'} value={name} autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) add.mutate(); }} />

        <span className="es-label">What goes in it</span>
        <div className="es-kindrow">
          <label>
            <input type="radio" name="albumkind" checked={kind === 'standard'}
              onChange={() => setKind('standard')} />
            <span>Photos</span>
          </label>
          <label>
            <input type="radio" name="albumkind" checked={kind === 'before_after'}
              onChange={() => setKind('before_after')} />
            <span>Before &amp; after</span>
          </label>
          <Hint label="Which should I pick?">
            Pick <strong>Photos</strong> unless the whole album is before/after
            shots — a photos album can still take the odd before/after set, and
            you add one from inside it.
          </Hint>
        </div>

        {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
      </div>
      <div className="es-modal__foot">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={add.isPending || !name.trim()} onClick={() => add.mutate()}>
          {add.isPending ? 'Adding…' : parentId ? 'Add sub-album' : 'Add album'}
        </Button>
      </div>
    </Modal>
  );
}

/* ── one album ────────────────────────────────────────────────────────── */

/* An album holds photos AND, optionally, sub-albums. Both are shown here rather
   than making the album pick a side, because splitting a Decking album into
   brands should not strand the general shots already in it. */
function AlbumView({ albumId, onBack, onOpenSub }: {
  albumId: string; onBack: () => void; onOpenSub: (id: string) => void;
}) {
  const qc = useQueryClient();
  const key = ['galleryItems', albumId];
  const { data } = useQuery({ queryKey: key, queryFn: () => getGalleryDetail(albumId) });
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['gallery'] });
    void qc.invalidateQueries({ queryKey: ['galleries'] });
  };

  const cat = data?.category;
  const subs = data?.children ?? [];
  const itemCount = data?.items.length ?? 0;

  const subIds = subs.map((s) => s.id);
  const saveSubs = (ids: string[]) => reorderSubAlbums(albumId, ids);
  const subOrder = useMoveOrder(subIds, saveSubs, { onSaved: refresh, onError: setErr });
  const subDrag = useDragOrder(subIds, saveSubs, { onSaved: refresh, onError: setErr });
  const subById = new Map(subs.map((s) => [s.id, s]));
  const subsShown = subDrag.ids.map((sid) => subById.get(sid)).filter((s): s is (typeof subs)[number] => !!s);

  /* An album is a set of PHOTOS or a set of SUB-ALBUMS, and the first thing put
     in it decides which. Both at once was the original design and it read as two
     half-empty lists stacked on one screen — an album of photos still showed an
     empty "Sub-albums 0" heading above them, and there was no clear answer to
     where the next photo belonged.

     `mixed` only happens to albums that predate this rule. They keep rendering
     both halves; the server refuses to add more of either until one is empty. */
  const split = subs.length > 0;
  const mixed = split && itemCount > 0;

  const rename = (name: string) =>
    updateGalleryCategory(albumId, { name }).then(refresh).catch((e) => setErr(msg(e, 'Could not rename.')));
  const describe = (description: string) =>
    updateGalleryCategory(albumId, { description }).then(refresh)
      .catch((e) => setErr(msg(e, 'Could not save the description.')));

  return (
    <div className="es-wrap">
      <div className="es-head">
        <Button variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={15} />} onClick={onBack}>Back</Button>
        <h2>{cat?.name ?? 'Album'}</h2>
        <p>{split ? 'This album is split into sub-albums. Photos live inside them.' : 'The photos in this album.'}</p>
      </div>

      {cat && <RenameRow name={cat.name} onRename={rename} />}
      {/* The column, the API and the document have carried this since 0060 —
          there was simply nowhere to type it, so every album shipped without
          one. */}
      {cat && <DescribeRow value={cat.description} onSave={describe} />}
      {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}

      {mixed && (
        <p className="es-hint">
          This album has both photos and sub-albums, which is no longer how albums
          work. Move its loose photos into a sub-album, or remove the sub-albums,
          and it will behave like every other album.
        </p>
      )}

      {/* ── split into sub-albums ── */}
      {split && (
        <>
          <div className="es-listlabel">
            <Icon name="layers" size={14} />
            <span>Sub-albums</span>
            <span className="es-listlabel__count">{subs.length}</span>
          </div>

          <div className={`es-gallist${subDrag.draggingId ? ' is-dragging' : ''}`}>
            {subsShown.map((s) => (
              /* The controls sit BESIDE the card, not in it: the card is itself
                 a button, and a button inside a button is invalid markup that
                 browsers resolve by swallowing the inner one's clicks. */
              <div className={`es-galrow${subDrag.draggingId === s.id ? ' is-held' : ''}`} key={s.id}
                ref={(el) => subDrag.register(s.id, el)}>
                <button type="button" className="es-galcard" onClick={() => onOpenSub(s.id)}>
                  <Thumb url={s.coverUrl} assetId={s.coverAssetId} />
                  <span className="es-galcard__name">
                    {s.name}
                    {s.kind === 'before_after' && <span className="es-galcard__tag">Before &amp; after</span>}
                  </span>
                  <span className="es-galcard__meta">
                    {s.itemCount ? `${s.itemCount} photo${s.itemCount === 1 ? '' : 's'}` : 'Empty'}
                  </span>
                  <span className="es-galcard__go"><Icon name="chevron-right" size={16} /></span>
                </button>
                {subIds.length > 1 && <DragGrip id={s.id} drag={subDrag} keys={subOrder} label="sub-album" />}
              </div>
            ))}
          </div>

          <Button variant="outline" size="sm" iconLeft={<Icon name="plus" size={15} />}
            onClick={() => { setAdding(true); setErr(''); }}>
            Add a sub-album
          </Button>

          {/* The same shape as removing an album a level up. The server refuses
              (409) while a sub-album still holds photos, and that message is the
              useful one — emptying it is a deliberate act, not a side effect of
              deleting the thing above it. */}
          <details className="es-danger">
            <summary>Remove a sub-album</summary>
            {subs.map((sub) => (
              <div key={sub.id} className="vault-add__row">
                <span>{sub.name}</span>
                <Button variant="ghost" size="sm" onClick={() => {
                  if (!confirm(`Remove the sub-album “${sub.name}”?`)) return;
                  setErr('');
                  deleteGalleryCategory(sub.id).then(refresh).catch((e) => setErr(msg(e, 'Could not remove it.')));
                }}>
                  Remove
                </Button>
              </div>
            ))}
          </details>
        </>
      )}

      {/* ── a plain album of photos ── */}
      {!split && (
        <>
          <div className="es-listlabel">
            <Icon name="image" size={14} />
            <span>Photos</span>
            <span className="es-listlabel__count">{itemCount}</span>
          </div>
          <ItemsView categoryId={albumId} embedded />

          {/* Offered only while it is still a choice. Once there are photos,
              splitting would strand them, so it is off the table until they are
              moved — the server refuses it either way. */}
          {!itemCount && (
            <div className="es-actionrow">
              <Button variant="ghost" size="sm" iconLeft={<Icon name="layers" size={15} />}
                onClick={() => { setAdding(true); setErr(''); }}>
                Split into sub-albums instead
              </Button>
              <Hint label="When to use sub-albums">
                Useful when an album has a lot of sub-categories — a Decking album
                with Trex, TimberTech and Azek, for example. An album holds photos
                or sub-albums, not both, so choose before you upload.
              </Hint>
            </div>
          )}
        </>
      )}

      {/* Legacy mixed album: its photos still need somewhere to be seen. */}
      {mixed && (
        <>
          <div className="es-listlabel">
            <Icon name="image" size={14} />
            <span>Loose photos</span>
            <span className="es-listlabel__count">{itemCount}</span>
          </div>
          <ItemsView categoryId={albumId} embedded />
        </>
      )}

      {adding && (
        <AddAlbumDialog parentId={albumId} title="New sub-album"
          onClose={() => setAdding(false)} onAdded={refresh} />
      )}
    </div>
  );
}

/* ── one gallery ──────────────────────────────────────────────────────── */

/* The manager's home screen. Named for the gallery it reads from, which is not
   a thing the client is ever shown. */
function GalleryView({ id, onBack, onOpenCategory }: {
  /** '' until the first album creates the gallery — the album list is empty
      then too, which is exactly what should be on screen. */
  id: string;
  onBack: () => void; onOpenCategory: (categoryId: string) => void;
}) {
  const qc = useQueryClient();
  const key = ['gallery', id];
  const { data } = useQuery({ queryKey: key, queryFn: () => getGallery(id), enabled: !!id });
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['galleries'] });
  };

  const categories = data?.categories ?? [];

  const albumIds = categories.map((cc) => cc.id);
  const saveAlbums = (ids: string[]) => reorderGalleryAlbums(id, ids);
  const albumOrder = useMoveOrder(albumIds, saveAlbums, { onSaved: refresh, onError: setErr });
  const albumDrag = useDragOrder(albumIds, saveAlbums, { onSaved: refresh, onError: setErr });
  const albumById = new Map(categories.map((cc) => [cc.id, cc]));
  const albumsShown = albumDrag.ids.map((aid) => albumById.get(aid)).filter((cc): cc is (typeof categories)[number] => !!cc);

  return (
    <div className="es-wrap">
      <div className="es-head">
        <Button variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={15} />} onClick={onBack}>Back</Button>
        <h2>Albums</h2>
        <p>Every album on your site. Photos live inside them; changes reach the site when you save.</p>
      </div>

      {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}

      <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={15} />} onClick={() => { setAdding(true); setErr(''); }}>
        Add an album
      </Button>
      {adding && (
        <AddAlbumDialog onClose={() => setAdding(false)} onAdded={refresh} />
      )}

      {!!categories.length && (
        <div className="es-listlabel">
          <Icon name="layers" size={14} />
          <span>Albums</span>
          <span className="es-listlabel__count">{categories.length}</span>
        </div>
      )}
      <div className={`es-gallist${albumDrag.draggingId ? ' is-dragging' : ''}`}>
        {albumsShown.map((c) => (
          <div className={`es-galrow${albumDrag.draggingId === c.id ? ' is-held' : ''}`} key={c.id}
            ref={(el) => albumDrag.register(c.id, el)}>
            <button type="button" className="es-galcard" onClick={() => onOpenCategory(c.id)}>
              <Thumb url={c.coverUrl} assetId={c.coverAssetId} />
              <span className="es-galcard__name">
                {c.name}
                {c.kind === 'before_after' && <span className="es-galcard__tag">Before &amp; after</span>}
                {c.hidden && <span className="es-galcard__tag">Hidden</span>}
              </span>
              <span className="es-galcard__meta">
                {c.itemCount ? `${c.itemCount} photo${c.itemCount === 1 ? '' : 's'}` : 'Empty'}
                {!!c.children.length && ` · ${c.children.length} sub-album${c.children.length === 1 ? '' : 's'}`}
              </span>
              <span className="es-galcard__go"><Icon name="chevron-right" size={16} /></span>
            </button>
            {albumIds.length > 1 && <DragGrip id={c.id} drag={albumDrag} keys={albumOrder} label="album" />}
          </div>
        ))}
      </div>

      {!categories.length && (
        <p className="es-hint">No albums yet — photos go inside an album, so add one to get started.</p>
      )}

      {/* Admin-only; renders nothing at all for everyone else. */}
      <div className="es-adminrow">
        <GallerySettingsButton />
        <GalleryApiCard />
      </div>

      {!!categories.length && (
        <details className="es-danger">
          <summary>Remove an album</summary>
          {categories.map((c) => (
            <div key={c.id} className="vault-add__row">
              <span>{c.name}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                if (!confirm(`Remove the album "${c.name}"?`)) return;
                setErr('');
                deleteGalleryCategory(c.id).then(refresh).catch((e) => setErr(msg(e, 'Could not remove it.')));
              }}>
                Remove
              </Button>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

/* ── the photos in one category (or in a single-page gallery) ─────────── */

function ItemsView({ categoryId, onBack, heading, onRename, extra, error, embedded }: {
  categoryId: string; onBack?: () => void;
  heading?: string;
  onRename?: (name: string) => void;
  extra?: React.ReactNode; error?: string;
  /** Rendered inside an album's page, so it drops its own header and back link. */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const key = ['galleryItems', categoryId];
  const { data } = useQuery({ queryKey: key, queryFn: () => getGalleryDetail(categoryId), enabled: !!categoryId });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inRef = useRef<HTMLInputElement>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['galleries'] });
    void qc.invalidateQueries({ queryKey: ['gallery'] });
  };

  const cat = data?.category;
  const items = data?.items ?? [];
  const isPairs = cat?.kind === 'before_after';
  const title = heading ?? cat?.name ?? 'Photos';


  const addPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true); setErr('');
    try {
      for (const f of Array.from(files)) await uploadGalleryPhoto(categoryId, await processImageSized(f));
      refresh();
    } catch (e) { setErr(msg(e, 'Upload failed.')); }
    finally { setBusy(false); if (inRef.current) inRef.current.value = ''; }
  };

  const addPair = () =>
    createGalleryPair(categoryId).then(refresh).catch((e) => setErr(msg(e, 'Could not add the set.')));

  const itemIds = items.map((i) => i.id);
  const saveOrder = (ids: string[]) => reorderGalleryItems(categoryId, ids);
  /* Two ways to reorder the same list, sharing one save. Drag is the one people
     reach for; the arrows in the photo dialog are the one a keyboard can use. */
  const drag = useDragOrder(itemIds, saveOrder, { onSaved: refresh, onError: setErr });
  const arrows = useMoveOrder(itemIds, saveOrder, { onSaved: refresh, onError: setErr });

  const byId = new Map(items.map((i) => [i.id, i]));
  const ordered = drag.ids.map((id) => byId.get(id)).filter((i): i is GalleryItem => !!i);
  const [openId, setOpenId] = useState('');
  const open = openId ? byId.get(openId) : undefined;
  const isCover = (item: GalleryItem) => !!cat?.coverAssetId
    && (item.assetId === cat.coverAssetId || item.beforeAssetId === cat.coverAssetId);
  const makeCover = (item: GalleryItem) =>
    updateGalleryCategory(categoryId, { coverAssetId: item.assetId || item.beforeAssetId })
      .then(refresh).catch((e) => setErr(msg(e, 'Could not set the cover.')));

  return (
    <div className="es-wrap">
      {!embedded && (
        <div className="es-head">
          <Button variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={15} />} onClick={onBack}>Back</Button>
          <h2>{title}</h2>
          <p>{isPairs
            ? 'Each pair stays together when you reorder.'
            : 'Photos appear in this order, and a before/after set moves as one.'}</p>
        </div>
      )}

      {!embedded && onRename && cat && <RenameRow name={title} onRename={onRename} />}
      {/* Sub-albums are albums: same row, same column, same place in the
          document. This screen is the only one that shows one on its own. */}
      {!embedded && cat && (
        <DescribeRow value={cat.description}
          onSave={(description) => updateGalleryCategory(categoryId, { description })
            .then(refresh).catch((e) => setErr(msg(e, 'Could not save the description.')))} />
      )}
      {(err || error) && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err || error}</p>}
      {extra}

      {isPairs ? (
        <Button variant="outline" size="sm" iconLeft={<Icon name="plus" size={15} />}
          onClick={() => addPair()}>
          Add a pair
        </Button>
      ) : (
        <>
          <div className="es-drop" onClick={() => inRef.current?.click()}>
            <span className="es-drop__ic"><Icon name="upload" size={20} /></span>
            <strong>{busy ? 'Uploading…' : 'Click to add photos'}</strong>
            <span>Added here straight away &mdash; on your site when you save</span>
            <input ref={inRef} type="file" accept="image/*" multiple hidden onChange={(e) => addPhotos(e.target.files)} />
          </div>
          {/* A pair in a photos album: it lands at the end like any other item,
              and reorders as one because it is a single row. */}
          <div className="es-actionrow">
            <Button variant="outline" size="sm" iconLeft={<Icon name="plus" size={15} />}
              onClick={() => addPair()}>
              Add a before/after set
            </Button>
            <Hint label="What is a before/after set?">
              Two shots of the same job that stay side by side and never get
              separated when you reorder the album.
            </Hint>
          </div>
        </>
      )}

      <div className={`es-grid${drag.draggingId ? ' is-dragging' : ''}`}>
        {ordered.map((item, n) => (
          <PhotoTile key={item.id} item={item} index={n} drag={drag}
            isCover={isCover(item)} onOpen={() => setOpenId(item.id)} />
        ))}
      </div>

      {!items.length && <p className="es-hint">Nothing here yet.</p>}

      {open && (
        <PhotoDialog item={open} ids={drag.ids} arrows={arrows}
          isCover={isCover(open)} onMakeCover={() => makeCover(open)}
          onChanged={refresh} onError={setErr} onClose={() => setOpenId('')} />
      )}
    </div>
  );
}

/* One photo in the grid.
 *
 * The tile carries the picture and the two facts worth seeing at a glance —
 * where it sits, and whether it is the cover. Everything else is behind a tap,
 * because a grid of tiles each wearing a text field and four buttons is a
 * spreadsheet, not a set of photographs. */
function PhotoTile({ item, index, drag, isCover, onOpen }: {
  item: GalleryItem; index: number; drag: ReturnType<typeof useDragOrder>;
  isCover: boolean; onOpen: () => void;
}) {
  const dragging = drag.draggingId === item.id;
  return (
    <div className={`es-tile${dragging ? ' is-held' : ''}`}
      ref={(el) => drag.register(item.id, el)}>
      <button type="button" className="es-tile__hit" onClick={onOpen}
        aria-label={`Edit ${item.alt || (item.type === 'pair' ? 'before and after set' : 'photo')} ${index + 1}`}>
        {item.type === 'pair' ? (
          <span className="es-tile__pair">
            <Thumbish url={item.beforeUrl} assetId={item.beforeAssetId} />
            <Thumbish url={item.afterUrl} assetId={item.afterAssetId} />
          </span>
        ) : (
          <Thumbish url={item.url} assetId={item.assetId} alt={item.alt} />
        )}
      </button>

      <span className="es-tile__pos">{index + 1}</span>
      <span className="es-tile__flags">
        {item.featured && <span className="es-tile__flag is-featured"><Icon name="star" size={11} /> Featured</span>}
        {isCover && <span className="es-tile__flag is-cover"><Icon name="image" size={11} /> Cover</span>}
      </span>
      {item.type === 'pair' && <span className="es-tile__kind">Before &amp; after</span>}
      {/* The way in, and the only control on the tile that says so. The picture
          itself opens the same dialog — this is what makes that discoverable. */}
      <button type="button" className="es-tile__gear" onClick={onOpen}
        title="Photo settings" aria-label={`Settings for photo ${index + 1}`}>
        <Icon name="settings" size={14} />
      </button>

      {/* The grip, not the tile: see useDragOrder on why a touch drag needs its
          own target rather than competing with the page's scroll. */}
      <span className="es-tile__grip" title="Drag to reorder" aria-hidden="true"
        {...drag.gripProps(item.id)}>
        <Icon name="grip" size={14} />
      </span>
    </div>
  );
}

function Thumbish({ url, assetId, alt = '' }: { url: string; assetId: string; alt?: string }) {
  return assetId
    ? <AssetImg className="es-tile__img" url={url} assetId={assetId} alt={alt} />
    : <span className="es-tile__img es-tile__img--empty" aria-hidden="true"><Icon name="image" size={18} /></span>;
}

/* One photo, opened.
 *
 * Alt text and the cover choice were inline on every row before this, which put
 * a text input under every thumbnail and made the album a form. They belong to
 * one photo at a time, so they live here. */
function PhotoDialog({ item, ids, arrows, isCover, onMakeCover, onChanged, onError, onClose }: {
  item: GalleryItem;
  ids: readonly string[];
  arrows: { move: (id: string, d: -1 | 1) => void; busy: boolean };
  isCover: boolean; onMakeCover: () => void;
  onChanged: () => void; onError: (m: string) => void; onClose: () => void;
}) {
  const { featuredUsed, featuredLimit } = useGalleries();
  const [featuring, setFeaturing] = useState(false);
  /* Disabled rather than left to be refused. The server enforces the cap and
     names what to do about it, but a button you press and watch fail is a worse
     way to meet a rule than one that is visibly spent. */
  const atLimit = featuredUsed >= featuredLimit;
  const [title, setTitle] = useState(item.title);
  const [alt, setAlt] = useState(item.alt);
  const [label, setLabel] = useState(item.label);
  /* Applies as you go, and that is safe now: it writes the DRAFT, not the site.
     Staging it behind a second button called Save would put two buttons of that
     name on one screen meaning different things — one that changes a field and
     one that publishes a gallery. The one worth naming is the one that goes
     live. */
  const save = (patch: { title?: string; alt?: string; label?: string }) =>
    updateGalleryItem(item.id, patch).then(onChanged).catch((e) => onError(msg(e, 'Could not save.')));

  /* Quarter turns chosen but not yet saved. Previewed with CSS so trying
     left-then-right costs nothing; the bytes are re-encoded once, on Save. */
  const [turn, setTurn] = useState(0);
  const [rotating, setRotating] = useState(false);
  useEffect(() => { setTurn(0); }, [item.assetId]);
  const saveRotation = async () => {
    setRotating(true);
    try {
      /* Read through the Worker, not the CDN: the CDN may still be holding the
         bytes from before an earlier turn, and turning those would undo it. */
      const raw = await apiBlob(`/api/studio/gallery-assets/${encodeURIComponent(item.assetId)}`, true);
      const ext = (item.url.split('?')[0].split('.').pop() || '').toLowerCase();
      const type = ({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' } as Record<string, string>)[ext] || raw.type;
      const turned = await rotateImage(new Blob([raw], { type }), turn, `photo.${ext || 'jpg'}`);
      await rotateGalleryAsset(item.assetId, turned);
      setTurn(0);
      onChanged();
    } catch (e) {
      onError(msg(e, 'Could not rotate the photo.'));
    } finally {
      setRotating(false);
    }
  };

  return (
    <Modal title={item.type === 'pair' ? 'Before & after set' : 'Photo'} onClose={onClose}>
      <div className="es-modal__body">
        <div className="es-photo__shot">
          {item.type === 'pair' ? (
            <div className="es-photo__pair">
              <Slot itemId={item.id} role="before" url={item.beforeUrl} assetId={item.beforeAssetId} onChanged={onChanged} onError={onError} />
              <Slot itemId={item.id} role="after" url={item.afterUrl} assetId={item.afterAssetId} onChanged={onChanged} onError={onError} />
            </div>
          ) : (
            item.assetId
              ? (
                <div className="es-photo__stage">
                  <AssetImg className={`es-photo__img es-photo__img--turn${turn}`} url={item.url} assetId={item.assetId} alt={item.alt || 'Photo'} />
                </div>
              )
              : <span className="es-photo__img es-photo__img--empty" />
          )}
        </div>

        {item.type !== 'pair' && item.assetId && (
          <>
            <span className="es-label">
              Rotate
              <Hint label="What does rotating change?">
                The photo itself, everywhere it is used &mdash; every album, case
                study and post, and your site &mdash; as soon as you apply the
                rotation. Its web address stays the same.
              </Hint>
            </span>
            <div className="es-actionrow">
              <Button variant="ghost" size="sm" disabled={rotating} aria-label="Rotate left"
                onClick={() => setTurn((t) => (t + 3) % 4)}>
                <Icon name="rotate-ccw" size={14} /> Left
              </Button>
              <Button variant="ghost" size="sm" disabled={rotating} aria-label="Rotate right"
                onClick={() => setTurn((t) => (t + 1) % 4)}>
                <Icon name="rotate-cw" size={14} /> Right
              </Button>
              {turn !== 0 && (
                <>
                  <Button variant="primary" size="sm" disabled={rotating} onClick={saveRotation}>
                    {rotating ? 'Applying…' : 'Apply rotation'}
                  </Button>
                  <Button variant="ghost" size="sm" disabled={rotating} onClick={() => setTurn(0)}>Undo</Button>
                </>
              )}
            </div>
          </>
        )}

        {item.type === 'pair' && (
          <>
            <span className="es-label">Label</span>
            <Input placeholder="e.g. Kitchen remodel, Edina" value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => { if (label !== item.label) save({ label }); }} />
          </>
        )}

        <span className="es-label">
          Title <span className="es-optional">(optional)</span>
          <Hint label="What is the title for?">
            A short name for what this shows &mdash; &ldquo;Cedar deck rebuild&rdquo;, not a
            sentence. Your site can use it as a heading above the photo.
          </Hint>
        </span>
        <Input placeholder="e.g. Cedar deck rebuild" value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => { if (title !== item.title) save({ title }); }} />

        {/* Its own field, not the caption. In the old model caption did both
            jobs, so a client's published alt text was whatever they typed as a
            caption. */}
        <span className="es-label">
          Describe the photo
          <Hint label="Why describe it?">
            This is read aloud by screen readers, and it is what search engines
            go on. Describe what is in the shot, not what it is called.
          </Hint>
        </span>
        <Input placeholder="Alt text — what someone who can't see it would need" value={alt}
          onChange={(e) => setAlt(e.target.value)}
          onBlur={() => { if (alt !== item.alt) save({ alt }); }} />

        <span className="es-label">
          Featured
          <Hint label="What does featuring do?">
            Offers this photo to your site for the places that are not the
            gallery &mdash; a strip on the homepage, a band on a service page. What
            your site does with it is up to its design. Your Triton team sets
            how many you can pick.
          </Hint>
        </span>
        <button type="button" className={`es-cover${item.featured ? ' is-on' : ''}`}
          aria-pressed={!!item.featured} disabled={featuring || (!item.featured && atLimit)}
          onClick={() => {
            setFeaturing(true);
            updateGalleryItem(item.id, { featured: !item.featured })
              .then(onChanged)
              .catch((e) => onError(msg(e, 'Could not change that.')))
              .finally(() => setFeaturing(false));
          }}>
          <Icon name="star" size={13} /> {item.featured ? 'Featured' : 'Feature this photo'}
        </button>
        {/* The count stays on screen rather than going in the tooltip: it is
            live state, and it is the reason the button is disabled. */}
        <span className="es-hint">
          {atLimit && !item.featured
            ? `All ${featuredLimit} featured slots are used — unfeature another photo first.`
            : `${featuredUsed} of ${featuredLimit} used.`}
        </span>

        <span className="es-label">
          Album cover
          <Hint label="What is the cover?">
            The photo that represents this album everywhere else in the studio
            and on your site. Without one, the first photo stands in.
          </Hint>
        </span>
        <button type="button" className={`es-cover${isCover ? ' is-on' : ''}`}
          aria-pressed={isCover} onClick={onMakeCover}>
          <Icon name="image" size={13} /> {isCover ? 'This is the cover' : 'Use as the album cover'}
        </button>


        {ids.length > 1 && (
          <>
            <span className="es-label">Position</span>
            <div className="es-actionrow">
              <MoveButtons id={item.id} ids={ids} order={arrows}
                label={item.type === 'pair' ? 'set' : 'photo'} />
              <Hint label="Other ways to reorder">
                You can also drag a photo by the grip in its bottom-right corner,
                back in the grid.
              </Hint>
            </div>
          </>
        )}
      </div>
      <div className="es-modal__foot">
        <Button variant="ghost" size="sm"
          onClick={() => {
            if (!confirm('Remove this from the gallery?')) return;
            deleteGalleryItem(item.id).then(() => { onChanged(); onClose(); })
              .catch((e) => onError(msg(e, 'Could not remove it.')));
          }}>
          <Icon name="trash" size={14} /> Remove
        </Button>
        <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}

/* A card's cover. Empty galleries get a placeholder rather than a collapsed
   row, so the list keeps one rhythm whether or not anything has been uploaded. */
function Thumb({ url, assetId }: { url: string; assetId: string }) {
  return assetId
    ? <AssetImg className="es-galcard__thumb" url={url} assetId={assetId} alt="" />
    : <span className="es-galcard__thumb es-galcard__thumb--empty" aria-hidden="true"><Icon name="image" size={16} /></span>;
}

/* An album's description: a line or two the site can print under its heading.
   Saved on blur like the name beside it, and like everything else here it
   changes the draft — it reaches the site when the gallery is saved. */
function DescribeRow({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  return (
    <div className="es-describe">
      <Textarea rows={2} placeholder="What is this album? A line or two, shown under its heading."
        value={text} onChange={(e) => setText(e.target.value)}
        onBlur={() => { if (text.trim() !== value) onSave(text.trim()); }} />
    </div>
  );
}

function RenameRow({ name, onRename }: { name: string; onRename: (n: string) => void }) {
  const [value, setValue] = useState(name);
  return (
    <div className="vault-add__row">
      <Input value={value} onChange={(e) => setValue(e.target.value)}
        onBlur={() => { if (value.trim() && value !== name) onRename(value.trim()); }} />
    </div>
  );
}

function Slot({ itemId, role, url, assetId, onChanged, onError }: {
  itemId: string; role: 'before' | 'after'; url: string; assetId: string;
  onChanged: () => void; onError: (m: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try { await uploadGallerySide(itemId, role, await processImageSized(files[0])); onChanged(); }
    catch (e) { onError(msg(e, 'Upload failed.')); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <button type="button" className={`es-slot${url ? ' is-filled' : ''}`} onClick={() => ref.current?.click()}>
      {assetId ? <AssetImg url={url} assetId={assetId} alt={role} /> : <span>{busy ? 'Uploading…' : `Add ${role}`}</span>}
      <span className="es-slot__tag">{role}</span>
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => pick(e.target.files)} />
    </button>
  );
}
